import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmt2 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function CheckoutPage() {
  const [cart, setCart] = useState({ items: [], subtotal_final: 0 });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Load cart once on mount
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/cart");
        setCart(r.data);
      } catch (error) {
        console.error("Failed to load cart:", error);
        toast.error("Failed to load cart. Please try again.");
      }
    })();
  }, []);

  // Pre-fill contact details when user session resolves
  useEffect(() => {
    if (user) {
      setName((prev) => prev || user.name || "");
      setEmail((prev) => prev || user.email || "");
    }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (cart.items.length === 0) { toast.error("Cart is empty"); return; }
    setSubmitting(true);
    try {
      const items = cart.items.map((i) => ({ listing_id: i.listing_id, date: i.date, guests: i.guests }));
      const r = await api.post("/checkout", { items, contact_email: email, contact_name: name, notes });
      toast.success("Booking created!");
      navigate(`/payments/${r.data.booking_group_id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Checkout failed"); }
    setSubmitting(false);
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-10" data-testid="checkout-page">
      <h1 className="font-display text-3xl font-semibold tracking-tight">Checkout</h1>
      <form onSubmit={submit} className="mt-6 grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2 grid gap-4">
          <div className="rounded-2xl bg-card border border-border p-5">
            <h2 className="font-semibold">Contact details</h2>
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              <label className="text-sm">Full name
                <Input required value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="checkout-name-input" />
              </label>
              <label className="text-sm">Email
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" data-testid="checkout-email-input" />
              </label>
            </div>
            <label className="block mt-3 text-sm">Notes for host (optional)
              <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" data-testid="checkout-notes-input" />
            </label>
          </div>

          <div className="rounded-2xl bg-card border border-border p-5">
            <h2 className="font-semibold">Your bookings</h2>
            <div className="mt-3 grid gap-2">
              {cart.items.map((it) => (
                <div key={it.cart_item_id} className="flex items-center gap-3 border-b border-border last:border-0 pb-2 last:pb-0">
                  <img src={it.listing.image} className="h-12 w-16 object-cover rounded-lg" alt="" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{it.listing.title}</div>
                    <div className="text-xs text-muted-foreground">{it.listing.location} · {it.date} · {it.guests} guests</div>
                  </div>
                  <div className="text-sm font-semibold">{fmt2(it.line_final)}</div>
                </div>
              ))}
              {cart.items.length === 0 && <p className="text-muted-foreground text-sm">Your cart is empty.</p>}
            </div>
          </div>
        </div>

        <aside className="lg:col-span-1">
          <div className="rounded-2xl bg-card border border-border p-5 lg:sticky lg:top-24" data-testid="checkout-summary">
            <h2 className="font-semibold mb-3">Order summary</h2>
            <div className="rounded-xl bg-muted border border-border p-3 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total</span>
              <span className="font-semibold" data-testid="checkout-price-breakdown-total">{fmt2(cart.subtotal_final)}</span>
            </div>
            <Button type="submit" className="mt-4 w-full rounded-xl h-11" disabled={submitting || cart.items.length === 0} data-testid="checkout-confirm-button">{submitting ? "Creating booking..." : "Continue to payment"}</Button>
            <p className="mt-3 text-xs text-muted-foreground">All-inclusive price. No hidden charges.</p>
          </div>
        </aside>
      </form>
    </div>
  );
}
